import { BookCallButton } from "@/components/shared/BookCallButton";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { SEOHead } from "@/components/shared/SEOHead";
import { SEOExpansion } from "@/components/shared/SEOExpansion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  TrendingUp,
  Rocket,
  ClipboardList,
  Headphones,
  Award,
  ShoppingBag,
  CheckCircle,
  ArrowRight,
  MapPin,
  Send,
  Loader2,
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const US_STATES = {
  alabama: { name: "Alabama", abbr: "AL", cities: ["Birmingham", "Montgomery", "Huntsville", "Mobile", "Tuscaloosa"] },
  alaska: { name: "Alaska", abbr: "AK", cities: ["Anchorage", "Fairbanks", "Juneau", "Sitka", "Ketchikan"] },
  arizona: { name: "Arizona", abbr: "AZ", cities: ["Phoenix", "Tucson", "Mesa", "Chandler", "Scottsdale"] },
  arkansas: { name: "Arkansas", abbr: "AR", cities: ["Little Rock", "Fort Smith", "Fayetteville", "Springdale", "Jonesboro"] },
  california: { name: "California", abbr: "CA", cities: ["Los Angeles", "San Francisco", "San Diego", "Sacramento", "San Jose"] },
  colorado: { name: "Colorado", abbr: "CO", cities: ["Denver", "Colorado Springs", "Aurora", "Fort Collins", "Lakewood"] },
  connecticut: { name: "Connecticut", abbr: "CT", cities: ["Hartford", "Bridgeport", "New Haven", "Stamford", "Waterbury"] },
  delaware: { name: "Delaware", abbr: "DE", cities: ["Wilmington", "Dover", "Newark", "Middletown", "Bear"] },
  florida: { name: "Florida", abbr: "FL", cities: ["Miami", "Orlando", "Tampa", "Jacksonville", "Fort Lauderdale"] },
  georgia: { name: "Georgia", abbr: "GA", cities: ["Atlanta", "Augusta", "Savannah", "Columbus", "Macon"] },
  hawaii: { name: "Hawaii", abbr: "HI", cities: ["Honolulu", "Hilo", "Kailua", "Pearl City", "Waipahu"] },
  idaho: { name: "Idaho", abbr: "ID", cities: ["Boise", "Meridian", "Nampa", "Idaho Falls", "Pocatello"] },
  illinois: { name: "Illinois", abbr: "IL", cities: ["Chicago", "Aurora", "Naperville", "Rockford", "Springfield"] },
  indiana: { name: "Indiana", abbr: "IN", cities: ["Indianapolis", "Fort Wayne", "Evansville", "South Bend", "Carmel"] },
  iowa: { name: "Iowa", abbr: "IA", cities: ["Des Moines", "Cedar Rapids", "Davenport", "Sioux City", "Waterloo"] },
  kansas: { name: "Kansas", abbr: "KS", cities: ["Wichita", "Overland Park", "Kansas City", "Olathe", "Topeka"] },
  kentucky: { name: "Kentucky", abbr: "KY", cities: ["Louisville", "Lexington", "Bowling Green", "Owensboro", "Covington"] },
  louisiana: { name: "Louisiana", abbr: "LA", cities: ["New Orleans", "Baton Rouge", "Shreveport", "Lafayette", "Lake Charles"] },
  maine: { name: "Maine", abbr: "ME", cities: ["Portland", "Lewiston", "Bangor", "South Portland", "Auburn"] },
  maryland: { name: "Maryland", abbr: "MD", cities: ["Baltimore", "Columbia", "Germantown", "Silver Spring", "Waldorf"] },
  massachusetts: { name: "Massachusetts", abbr: "MA", cities: ["Boston", "Worcester", "Springfield", "Cambridge", "Lowell"] },
  michigan: { name: "Michigan", abbr: "MI", cities: ["Detroit", "Grand Rapids", "Warren", "Sterling Heights", "Lansing"] },
  minnesota: { name: "Minnesota", abbr: "MN", cities: ["Minneapolis", "Saint Paul", "Rochester", "Bloomington", "Duluth"] },
  mississippi: { name: "Mississippi", abbr: "MS", cities: ["Jackson", "Gulfport", "Southaven", "Hattiesburg", "Biloxi"] },
  missouri: { name: "Missouri", abbr: "MO", cities: ["Kansas City", "St. Louis", "Springfield", "Columbia", "Independence"] },
  montana: { name: "Montana", abbr: "MT", cities: ["Billings", "Missoula", "Great Falls", "Bozeman", "Helena"] },
  nebraska: { name: "Nebraska", abbr: "NE", cities: ["Omaha", "Lincoln", "Bellevue", "Grand Island", "Kearney"] },
  nevada: { name: "Nevada", abbr: "NV", cities: ["Las Vegas", "Henderson", "Reno", "North Las Vegas", "Sparks"] },
  "newhampshire": { name: "New Hampshire", abbr: "NH", cities: ["Manchester", "Nashua", "Concord", "Derry", "Dover"] },
  "newjersey": { name: "New Jersey", abbr: "NJ", cities: ["Newark", "Jersey City", "Paterson", "Elizabeth", "Edison"] },
  "newmexico": { name: "New Mexico", abbr: "NM", cities: ["Albuquerque", "Las Cruces", "Rio Rancho", "Santa Fe", "Roswell"] },
  "newyork": { name: "New York", abbr: "NY", cities: ["New York City", "Buffalo", "Rochester", "Syracuse", "Albany"] },
  "northcarolina": { name: "North Carolina", abbr: "NC", cities: ["Charlotte", "Raleigh", "Greensboro", "Durham", "Winston-Salem"] },
  "northdakota": { name: "North Dakota", abbr: "ND", cities: ["Fargo", "Bismarck", "Grand Forks", "Minot", "West Fargo"] },
  ohio: { name: "Ohio", abbr: "OH", cities: ["Columbus", "Cleveland", "Cincinnati", "Toledo", "Akron"] },
  oklahoma: { name: "Oklahoma", abbr: "OK", cities: ["Oklahoma City", "Tulsa", "Norman", "Broken Arrow", "Edmond"] },
  oregon: { name: "Oregon", abbr: "OR", cities: ["Portland", "Salem", "Eugene", "Gresham", "Hillsboro"] },
  pennsylvania: { name: "Pennsylvania", abbr: "PA", cities: ["Philadelphia", "Pittsburgh", "Allentown", "Erie", "Reading"] },
  "rhodeisland": { name: "Rhode Island", abbr: "RI", cities: ["Providence", "Warwick", "Cranston", "Pawtucket", "East Providence"] },
  "southcarolina": { name: "South Carolina", abbr: "SC", cities: ["Charleston", "Columbia", "North Charleston", "Mount Pleasant", "Greenville"] },
  "southdakota": { name: "South Dakota", abbr: "SD", cities: ["Sioux Falls", "Rapid City", "Aberdeen", "Brookings", "Watertown"] },
  tennessee: { name: "Tennessee", abbr: "TN", cities: ["Nashville", "Memphis", "Knoxville", "Chattanooga", "Clarksville"] },
  texas: { name: "Texas", abbr: "TX", cities: ["Houston", "Dallas", "San Antonio", "Austin", "Fort Worth"] },
  utah: { name: "Utah", abbr: "UT", cities: ["Salt Lake City", "West Valley City", "Provo", "West Jordan", "Orem"] },
  vermont: { name: "Vermont", abbr: "VT", cities: ["Burlington", "Essex", "South Burlington", "Colchester", "Rutland"] },
  virginia: { name: "Virginia", abbr: "VA", cities: ["Virginia Beach", "Norfolk", "Chesapeake", "Richmond", "Newport News"] },
  washington: { name: "Washington", abbr: "WA", cities: ["Seattle", "Spokane", "Tacoma", "Vancouver", "Bellevue"] },
  "westvirginia": { name: "West Virginia", abbr: "WV", cities: ["Charleston", "Huntington", "Morgantown", "Parkersburg", "Wheeling"] },
  wisconsin: { name: "Wisconsin", abbr: "WI", cities: ["Milwaukee", "Madison", "Green Bay", "Kenosha", "Racine"] },
  wyoming: { name: "Wyoming", abbr: "WY", cities: ["Cheyenne", "Casper", "Laramie", "Gillette", "Rock Springs"] },
};

const OFFERS = [
  { icon: TrendingUp, title: "Expansion Support", desc: "Strategies to expand and grow your Total Wireless Store in {state}." },
  { icon: Rocket, title: "New Store Launch Assistance", desc: "Guidance and training to make your store launch go as smoothly as possible." },
  { icon: ClipboardList, title: "Smart Strategic Planning", desc: "Step-by-step guidance from our industry experts." },
  { icon: Headphones, title: "Specialized Help Desk", desc: "Continuous assistance for your operations, including marketing and client service." },
  { icon: Award, title: "Growth Incentives", desc: "Various programs to reward your store's growth." },
  { icon: ShoppingBag, title: "Customer Solutions", desc: "No-contract plans, latest devices, and accessories to attract customers." },
];

const WHY_PARTNER = [
  { title: "Exclusive Access & Insights", desc: "Leverage our direct ties with Total Wireless as an established Master Agent." },
  { title: "Complete Store Support", desc: "We assist with every aspect of store growth, from construction to marketing tactics and strategies." },
  { title: "Proven Experience in the Wireless Industry", desc: "Rely on our years of proven experience to make smarter business decisions." },
  { title: "Competitive Advantage", desc: "Always get the most up-to-date wireless technology, pricing, and marketing solutions." },
  { title: "24/7 Assistance", desc: "Our support team is always ready to help you face business challenges." },
];

const OUR_PROCESS = [
  { title: "Understanding Your Needs", desc: "We will align our assistance with your vision." },
  { title: "Choose the Right Location", desc: "We will help you find the best location for your new store." },
  { title: "Complete Assistance", desc: "Our experts will help craft a space that draws maximum customers." },
  { title: "Grow Confidently", desc: "Strategies that attract and retain customers long-term." },
];

function ContactForm({ stateName }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/state-inquiries`, { ...form, state: stateName });
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="state-form-success">
        <CheckCircle className="w-16 h-16 text-teal mx-auto mb-4" />
        <h3 className="font-heading font-bold text-xl text-navy mb-2">Thank You!</h3>
        <p className="text-slate-600 font-body">A representative will contact you shortly about opening a store in {stateName}.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8" data-testid="state-contact-form">
      <h3 className="font-heading font-bold text-xl text-navy mb-6">Get in Touch</h3>
      <div className="space-y-4">
        <Input
          placeholder="First Name *"
          value={form.first_name}
          onChange={(e) => setForm({ ...form, first_name: e.target.value })}
          required
          data-testid="state-form-firstname"
        />
        <Input
          placeholder="Last Name *"
          value={form.last_name}
          onChange={(e) => setForm({ ...form, last_name: e.target.value })}
          required
          data-testid="state-form-lastname"
        />
        <Input
          type="email"
          placeholder="Email *"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
          data-testid="state-form-email"
        />
        <Input
          type="tel"
          placeholder="Phone Number *"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          required
          data-testid="state-form-phone"
        />
        <Textarea
          placeholder="Tell us about your interest in opening a store..."
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          rows={4}
          data-testid="state-form-message"
        />
        <Button
          type="submit"
          disabled={submitting}
          className="w-full bg-teal hover:bg-teal/90 text-white font-body"
          data-testid="state-form-submit"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
          Submit
        </Button>
      </div>
    </form>
  );
}

export default function OpenStorePage() {
  const { stateSlug } = useParams();
  const stateData = US_STATES[stateSlug];

  if (!stateData) {
    return (
      <PublicLayout>
        <section className="bg-navy py-20">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h1 className="font-heading font-bold text-4xl text-white mb-4">State Not Found</h1>
            <p className="text-slate-300 font-body mb-8">We couldn't find that state. Browse all available states below.</p>
            <Link to="/open-a-store" className="inline-flex items-center gap-2 bg-teal text-white px-6 py-3 rounded-full font-body hover:bg-teal/90 transition-colors">
              View All States <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </PublicLayout>
    );
  }

  const { name: stateName, cities } = stateData;

  return (
    <PublicLayout>
      <SEOHead
        title={`Open a Total Wireless Store in ${stateName} | Prime Media Wireless`}
        description={`Partner with Prime Media Wireless to launch or expand your Total Wireless store in ${stateName}. Expert training, tools, and ongoing support for wireless retail success.`}
      />

      {/* Hero + Contact Form */}
      <section className="bg-navy py-16 lg:py-24" data-testid="state-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <p className="label-style text-teal mb-4">{stateName}</p>
              <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6 leading-tight">
                Grow Your Wireless Business in {stateName}
              </h1>
              <p className="text-slate-300 text-base lg:text-lg font-body mb-4">
                At Prime Media Wireless, we're actively seeking motivated dealers to open Total Wireless stores across {stateName}.
              </p>
              <p className="text-slate-300 text-base lg:text-lg font-body mb-4">
                With years of experience as a Total Wireless Master Agent, we provide the knowledge, strategies, and ongoing support you need to open and operate your new Total Wireless store successfully.
              </p>
              <p className="text-slate-300 text-base lg:text-lg font-body">
                Whether you're launching your first store or expanding further in {stateName}, our expert team is ready to help you at every stage.
              </p>
              <BookCallButton state={stateName} className="mt-6" />
              {cities && cities.length > 0 && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {cities.map((city) => (
                    <span key={city} className="inline-flex items-center gap-1 px-3 py-1.5 bg-white/10 text-white/80 text-xs font-body rounded-full">
                      <MapPin className="w-3 h-3" /> {city}
                    </span>
                  ))}
                </div>
              )}
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <ContactForm stateName={stateName} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 lg:py-20 bg-white" data-testid="state-offers">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy">What We Offer</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {OFFERS.map((offer, i) => {
              const Icon = offer.icon;
              return (
                <motion.div
                  key={offer.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="p-6 rounded-xl border border-slate-200 hover:border-teal/30 hover:shadow-md transition-all"
                  data-testid={`state-offer-${i}`}
                >
                  <div className="w-12 h-12 bg-teal/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-teal" />
                  </div>
                  <h3 className="font-heading font-semibold text-navy text-lg mb-2">{offer.title}</h3>
                  <p className="text-slate-600 font-body text-sm">{offer.desc.replace("{state}", stateName)}</p>
                </motion.div>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <a href="tel:2485900000" className="inline-flex items-center gap-2 bg-teal text-white px-8 py-3 rounded-full font-body font-semibold hover:bg-teal/90 transition-colors" data-testid="state-call-cta">
              Call Now: (248) 590-0000
            </a>
          </div>
        </div>
      </section>

      {/* Store Opportunities */}
      <section className="py-16 lg:py-20 bg-slate-50" data-testid="state-opportunities">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-6">
            Total Wireless Store Opportunities in {stateName}
          </h2>
          <p className="text-slate-600 font-body text-base lg:text-lg mb-4">
            If you are ready to start your wireless retail business in {stateName}, we are your dependable wireless master agent.
            To make your store successful in the {stateName} wireless market, we'll support you fully by providing in-store
            training and on-the-ground assistance.
          </p>
          <p className="text-slate-600 font-body text-base lg:text-lg mb-8">
            We will also assist you in expanding your client base and your profit, not only by configuring your business,
            but also by managing it in day-to-day operations.
          </p>
          <a
            href="#state-hero"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="inline-flex items-center gap-2 bg-teal text-white px-8 py-3 rounded-full font-body font-semibold hover:bg-teal/90 transition-colors"
            data-testid="state-get-in-touch-cta"
          >
            Get in Touch <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Why Partner + Our Process */}
      <section className="py-16 lg:py-20 bg-white" data-testid="state-why-partner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Why Partner */}
            <div>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-8">
                Why Partner With Prime Media Wireless in {stateName}?
              </h2>
              <ul className="space-y-4">
                {WHY_PARTNER.map((item, i) => (
                  <li key={i} className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-teal mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-heading font-semibold text-navy">{item.title}</span>
                      <span className="text-slate-600 font-body"> — {item.desc}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            {/* Our Process */}
            <div>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-8">Our Process</h2>
              <ol className="space-y-6">
                {OUR_PROCESS.map((step, i) => (
                  <li key={i} className="flex gap-4">
                    <div className="w-10 h-10 bg-teal/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="font-heading font-bold text-teal">{i + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-navy">{step.title}</h3>
                      <p className="text-slate-600 font-body text-sm">{step.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-20 bg-navy" data-testid="state-final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white mb-6">
            Let Us Help Your {stateName} Wireless Store Thrive!
          </h2>
          <p className="text-slate-300 font-body text-base lg:text-lg mb-4">
            Take your wireless business in {stateName} to the next level! At Prime Media Wireless, we combine
            industry knowledge with hands-on support to help you grow faster and stronger.
          </p>
          <p className="text-slate-300 font-body text-base lg:text-lg mb-8">
            Being a reliable Total Wireless Master Agent, we will ensure your store's long-term success.
            Contact us today to learn how we can realize your dream of owning a Total Wireless store in {stateName}.
          </p>
          <a
            href="#state-hero"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="inline-flex items-center gap-2 bg-teal text-white px-8 py-3 rounded-full font-body font-semibold hover:bg-teal/90 transition-colors"
            data-testid="state-final-get-in-touch"
          >
            Get in Touch <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Browse Other States */}
      <section className="py-12 bg-slate-50 border-t border-slate-200" data-testid="state-browse-states">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="font-heading font-semibold text-navy text-lg mb-4 text-center">Open a Store in Other States</h3>
          <div className="flex flex-wrap justify-center gap-2">
            {Object.entries(US_STATES)
              .filter(([slug]) => slug !== stateSlug)
              .map(([slug, data]) => (
                <Link
                  key={slug}
                  to={`/${slug}`}
                  className="px-3 py-1.5 text-xs font-body text-slate-600 bg-white border border-slate-200 rounded-full hover:border-teal hover:text-teal transition-colors"
                >
                  {data.name}
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* Internal Cross-Links */}
      <section className="py-12 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-sm text-slate-400 font-body mb-4">Explore More</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/totalwirelessmasteragent" className="text-sm font-body text-teal hover:underline">Total Wireless Master Agent</Link>
            <span className="text-slate-300">·</span>
            <Link to="/become-dealer" className="text-sm font-body text-teal hover:underline">Become a Dealer</Link>
            <span className="text-slate-300">·</span>
            <Link to="/locations" className="text-sm font-body text-teal hover:underline">Store Locations</Link>
            <span className="text-slate-300">·</span>
            <Link to="/careers" className="text-sm font-body text-teal hover:underline">Careers</Link>
            <span className="text-slate-300">·</span>
            <Link to="/our-process" className="text-sm font-body text-teal hover:underline">Our Process</Link>
            <span className="text-slate-300">·</span>
            <Link to="/faqs" className="text-sm font-body text-teal hover:underline">FAQs</Link>
          </div>
        </div>
      </section>
      <SEOExpansion stateName={stateName} />
    </PublicLayout>
  );
}

export { US_STATES };
